import React, { useEffect, useState } from "react";
import { User, UserRole } from "../../types/user.type";
import useGetRequest from "../../hooks/useGetRequest";
import dayjs from "dayjs";
import {
  Button,
  Form,
  Input,
  message,
  Modal,
  Select,
  Spin,
  Table,
  Typography,
  Popconfirm,
  InputNumber,
  Menu,
  Dropdown,
  Space,
} from "antd";
import { api } from "../../api/api";
import { Currency } from "../../types/currency.type";
import { renderTime } from "../../utils/renderTime";
import { DownOutlined, KeyOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/auth.context";
import { canEditStaff, hasAdminAccess } from "../../utils/roleUtils";

const { Title } = Typography;
const { Option } = Select;

export default function Staff() {
  const { authState } = useAuth();
  const isAdmin = hasAdminAccess(authState.user?.role);
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const { data, loading, refresh } = useGetRequest(`/user`);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState<boolean>(false);
  const [passwordLoading, setPasswordLoading] = useState<boolean>(false);
  const [userPassword, setUserPassword] = useState<{ password: string; username: string; name: string } | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (data) {
      setUsers(data.users);
    }
  }, [data]);

  const handleCreateUser = async (values: Partial<User>) => {
    setConfirmLoading(true);
    try {
      await api.post(`/user`, values);
      message.success("Пользователь успешно добавлен.");
      refresh();
      setIsModalOpen(false);
      setConfirmLoading(false);
      form.resetFields();
    } catch (error: any) {
      setConfirmLoading(false);
      console.log(error.response.data.error);
      message.error(error.response.data.error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      await api.delete(`/user/${userId}`);
      message.success("Пользователь успешно удалён.");
      refresh();
    } catch (error: any) {
      message.error("Ошибка при удалении пользователя.");
    }
  };

  const handleViewPassword = async (user: User) => {
    if (!isAdmin) {
      message.error("Только админ может просматривать пароли.");
      return;
    }
    setPasswordLoading(true);
    setPasswordModalOpen(true);
    try {
      const response = await api.get(`/user/${user._id}/password`);
      setUserPassword(response.data);
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Не удалось получить пароль.");
      setPasswordModalOpen(false);
    } finally {
      setPasswordLoading(false);
    }
  };

  const columns = [
    { title: "Имя", dataIndex: "name", key: "name" },
    { title: "Логин", dataIndex: "username", key: "username" },
    { title: "Роль", dataIndex: "role", key: "role" },
    {
      title: "Норма работы",
      dataIndex: "minuteNorm",
      key: "minuteNorm",
      render: (minuteNorm: number) => renderTime(minuteNorm),
    },
    {
      title: "Тип зарплаты",
      dataIndex: "salaryType",
      key: "salaryType",
    },
    {
      title: "Валюта зарплаты",
      dataIndex: "salaryCurrency",
      key: "salaryCurrency",
    },
    {
      title: "Создан",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => dayjs(date).format("DD.MM.YYYY HH:mm"),
    },
      {
        title: "",
        key: "actions",
        render: (_: any, user: User) => {
        const menu = (
          <Menu>
            {isAdmin && (
              <Menu.Item
                icon={<KeyOutlined />}
                onClick={() => handleViewPassword(user)}
              >
                Просмотреть пароль
              </Menu.Item>
            )}
            <Popconfirm
              title="Вы уверены, что хотите удалить этого пользователя?"
              onConfirm={() => handleDeleteUser(user._id)}
              okText="Да"
              cancelText="Нет"
            >
              <Menu.Item>Удалить</Menu.Item>
            </Popconfirm>
            {(user.role === UserRole.Admin ||
              user.role === UserRole.Cashier ||
              user.role === UserRole.Manager) && (
              <Menu.Item
                onClick={() => {
                  navigate(`/${user._id}/owner/${user.name}/orders`);
                }}
              >
                Смотреть заказы
              </Menu.Item>
            )}
            {user.role === UserRole.WarehouseMgr && (
              <>
                <Menu.Item
                  onClick={() => {
                    navigate(
                      `/${user._id}/${user.name}/warehouseMgrId/reports`
                    );
                  }}
                >
                  Смотреть отчеты
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    navigate(`/${user._id}/${user.name}/worklogs`);
                  }}
                >
                  Смотреть смены
                </Menu.Item>
              </>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title>Персонал</Title>
                        {canEditStaff(authState.user?.role) && (
                  <Button
                    type="primary"
                    onClick={() => setIsModalOpen(true)}
                    style={{ marginBottom: 16 }}
                  >
                    Добавить
                  </Button>
                )}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={users}
          rowKey={(record) => record._id}
          pagination={false}
        />
      )}

      <Modal
        title="Добавить пользователя"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateUser}>
          <Form.Item
            name="name"
            label="Имя"
            rules={[{ required: true, message: "Введите имя" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="username"
            label="Логин"
            rules={[{ required: true, message: "Введите логин" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="password"
            label="Пароль"
            rules={[{ required: true, message: "Введите пароль" }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="role"
            label="Роль"
            rules={[{ required: true, message: "Выберите роль" }]}
          >
            <Select placeholder="Выберите роль">
              {Object.values(UserRole).map((role) => (
                <Option key={role} value={role}>
                  {role}
                </Option>
              ))}
            </Select>
          </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={`Пароль пользователя: ${userPassword?.name || ""}`}
          open={passwordModalOpen}
          onCancel={() => {
            setPasswordModalOpen(false);
            setUserPassword(null);
          }}
          footer={[
            <Button key="close" onClick={() => {
              setPasswordModalOpen(false);
              setUserPassword(null);
            }}>
              Закрыть
            </Button>,
          ]}
          width={500}
        >
          {passwordLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Typography.Text>Загрузка...</Typography.Text>
            </div>
          ) : userPassword ? (
            <Space direction="vertical" style={{ width: "100%" }} size="large">
              <div>
                <Typography.Text strong>Логин: </Typography.Text>
                <Typography.Text>{userPassword.username}</Typography.Text>
              </div>
              <div>
                <Typography.Text strong>Пароль: </Typography.Text>
                <Input.Password
                  value={userPassword.password}
                  readOnly
                  style={{ marginTop: 8 }}
                />
              </div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Только админ может просматривать пароли пользователей.
              </Typography.Text>
            </Space>
          ) : null}
        </Modal>
      </div>
    );
  }
